import * as React from "react";
import { PageInfoButton, type PageInfoButtonProps } from "@/components/page-info/PageInfoButton";
import type { PageInfoId } from "@/lib/i18n/types/pageInfo";
import { cn } from "@/lib/utils";

/**
 * The single source of truth for page-top H1 styling. Every top-level
 * page heading (Dashboard, Documents, Projects, Project Management, …)
 * renders through this component so the font family, size, weight, and
 * style stay consistent across the app — Montserrat 700 at text-xl,
 * inherited from the `font-sans` token on `body`.
 *
 * Use the `as` prop to render a different element (e.g. `h2` for a
 * page section that visually matches the page title but should not be
 * the document's H1).
 *
 * `info` adds the page's ⓘ ("About this page") to the right of the title.
 * The button sits beside the heading, not inside it, so the heading's
 * accessible name stays the bare title. Outer spacing then belongs on
 * `wrapperClassName`, the row that holds both.
 */
export interface PageTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  as?: "h1" | "h2";
  info?: PageInfoId;
  /** Scope for the info dialog's permission checks; see {@link PageInfoButtonProps}. */
  infoProjectId?: PageInfoButtonProps["projectId"];
  wrapperClassName?: string;
}

export const PageTitle = React.forwardRef<HTMLHeadingElement, PageTitleProps>(function PageTitle(
  { as: Tag = "h1", className, children, info, infoProjectId, wrapperClassName, ...props },
  ref
) {
  const heading = (
    <Tag ref={ref} className={cn("text-xl font-bold", className)} {...props}>
      {children}
    </Tag>
  );
  if (!info) return heading;

  return (
    <div className={cn("flex min-w-0 items-center gap-1.5", wrapperClassName)}>
      {heading}
      <PageInfoButton page={info} projectId={infoProjectId} />
    </div>
  );
});
